import React, { useState, useEffect, useMemo } from 'react';

const NotificationSelector = ({ onChange, initialData }) => {
    const [enabled, setEnabled] = useState(!!initialData?.enabled);

    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);

    const [selectedDate, setSelectedDate] = useState(() => {
        if (!initialData?.date) return tomorrow;
        const parsed = new Date(`${initialData.date}T00:00:00`);
        return Number.isNaN(parsed.getTime()) ? tomorrow : parsed;
    });
    const [selectedTime, setSelectedTime] = useState(initialData?.time || '09:00');
    const [viewDate, setViewDate] = useState(() => new Date());

    const calendarData = useMemo(() => {
        const year = viewDate.getFullYear();
        const month = viewDate.getMonth();

        const firstDayOfMonth = new Date(year, month, 1).getDay();
        const firstDayIndex = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;

        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const daysInPrevMonth = new Date(year, month, 0).getDate();

        const days = [];

        for (let i = firstDayIndex - 1; i >= 0; i--) {
            days.push({ day: daysInPrevMonth - i, month: month - 1, year, current: false });
        }

        for (let i = 1; i <= daysInMonth; i++) {
            days.push({ day: i, month, year, current: true });
        }

        const remaining = 42 - days.length;
        for (let i = 1; i <= remaining; i++) {
            days.push({ day: i, month: month + 1, year, current: false });
        }

        return days;
    }, [viewDate]);

    const monthName = viewDate.toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' });
    const weekDays = ['Pt', 'Sa', 'Ça', 'Pe', 'Cu', 'Ct', 'Pz'];

    const handlePrevMonth = (e) => {
        e.stopPropagation();
        setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
    };

    const handleNextMonth = (e) => {
        e.stopPropagation();
        setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));
    };

    const isSelected = (d, m, y) => {
        return selectedDate.getDate() === d && selectedDate.getMonth() === m && selectedDate.getFullYear() === y;
    };

    const isToday = (d, m, y) => {
        const today = new Date();
        return today.getDate() === d && today.getMonth() === m && today.getFullYear() === y;
    };

    const isPast = (d, m, y) => {
        const date = new Date(y, m, d);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return date < today;
    };

    const handleSelectDate = (d, m, y) => {
        if (isPast(d, m, y)) return;
        const newDate = new Date(y, m, d);
        setSelectedDate(newDate);
    };

    useEffect(() => {
        if (onChange) {
            onChange({
                enabled,
                date: selectedDate.toISOString().split('T')[0],
                time: selectedTime
            });
        }
    }, [enabled, selectedDate, selectedTime, onChange]);

    return (
        <div className="bg-white dark:bg-gray-900/40 rounded-[2rem] border border-gray-100 dark:border-gray-800 shadow-sm overflow-hidden transition-all duration-500">
            <div
                onClick={() => setEnabled(!enabled)}
                className="flex items-center justify-between p-5 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors"
            >
                <div className="flex items-center gap-4">
                    <div className={`size-12 rounded-2xl flex items-center justify-center transition-all duration-500 ${enabled ? 'bg-primary text-white scale-110 shadow-lg shadow-primary/30' : 'bg-gray-100 dark:bg-gray-800 text-gray-400'}`}>
                        <span className="material-symbols-outlined text-[24px]">
                            {enabled ? 'calendar_today' : 'notifications_paused'}
                        </span>
                    </div>
                    <div>
                        <h4 className={`text-sm font-black tracking-tight ${enabled ? 'text-gray-900 dark:text-white' : 'text-gray-500'}`}>
                            {enabled ? 'Takvim Aktif' : 'Özel Hatırlatıcı'}
                        </h4>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">
                            {enabled ? `${selectedDate.toLocaleDateString('tr-TR')} @ ${selectedTime}` : 'Tarih ve saat seçin'}
                        </p>
                    </div>
                </div>

                <div className={`w-14 h-8 rounded-full p-1.5 transition-colors duration-500 flex items-center ${enabled ? 'bg-primary' : 'bg-gray-200 dark:bg-gray-700'}`}>
                    <div className={`size-5 bg-white rounded-full shadow-lg transform transition-transform duration-500 ease-out ${enabled ? 'translate-x-6 scale-90' : 'translate-x-0'}`} />
                </div>
            </div>

            <div className={`transition-all duration-700 ease-in-out px-6 overflow-hidden ${enabled ? 'max-h-[600px] mb-6 opacity-100 py-2' : 'max-h-0 opacity-0'}`}>

                <div className="h-px bg-gradient-to-r from-transparent via-gray-200 dark:via-gray-700 to-transparent mb-6" />

                <div className="flex items-center justify-between mb-4 px-2">
                    <button onClick={handlePrevMonth} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors text-gray-500">
                        <span className="material-symbols-outlined text-[20px]">chevron_left</span>
                    </button>
                    <span className="text-xs font-black uppercase tracking-widest text-gray-700 dark:text-gray-300">
                        {monthName}
                    </span>
                    <button onClick={handleNextMonth} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors text-gray-500">
                        <span className="material-symbols-outlined text-[20px]">chevron_right</span>
                    </button>
                </div>

                <div className="grid grid-cols-7 gap-1 mb-6 text-center">
                    {weekDays.map((wd) => (
                        <div key={wd} className="text-[10px] font-black text-primary/40 pb-2 uppercase">{wd}</div>
                    ))}
                    {calendarData.map((d, i) => (
                        <button
                            key={i}
                            disabled={isPast(d.day, d.month, d.year)}
                            onClick={() => handleSelectDate(d.day, d.month, d.year)}
                            className={`
                                relative size-9 rounded-xl text-[11px] font-bold transition-all flex items-center justify-center
                                ${!d.current ? 'text-gray-300 dark:text-gray-700' : 'text-gray-600 dark:text-gray-300'}
                                ${isSelected(d.day, d.month, d.year) ? 'bg-primary text-white shadow-lg shadow-primary/30 z-10' : 'hover:bg-gray-50 dark:hover:bg-gray-800'}
                                ${isToday(d.day, d.month, d.year) && !isSelected(d.day, d.month, d.year) ? 'border border-primary/30 text-primary' : ''}
                                ${isPast(d.day, d.month, d.year) ? 'opacity-20 cursor-not-allowed' : ''}
                            `}
                        >
                            {d.day}
                        </button>
                    ))}
                </div>

                <div className="space-y-3">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-2 pl-2">
                        <span className="material-symbols-outlined text-[16px]">schedule</span> Saat Ayarı
                    </label>
                    <div className="relative group">
                        <input
                            type="time"
                            value={selectedTime}
                            onChange={(e) => setSelectedTime(e.target.value)}
                            className="w-full h-14 bg-gray-50/50 dark:bg-gray-800/80 border-2 border-transparent focus:border-primary/20 rounded-2xl px-6 text-lg font-black tracking-[0.2em] text-gray-900 dark:text-white outline-none transition-all"
                        />
                        <div className="absolute right-5 top-1/2 -translate-y-1/2 pointer-events-none text-primary/30">
                            <span className="material-symbols-outlined">edit</span>
                        </div>
                    </div>
                </div>

                <p className="mt-4 text-[9px] font-bold text-gray-400 text-center uppercase tracking-tighter opacity-70">
                    * Liste oluşturulduğunda bu zaman için push bildirimi planlanacaktır.
                </p>

            </div>
        </div>
    );
};

export default NotificationSelector;
